/**
 * Design System Module
 *
 * Templates are design systems, not page layouts. Each template exports:
 * - allowedComponents: which structural blocks the LLM may use
 * - typographyTokens: font families, scale, weights
 * - colorTokens: palette as CSS custom properties
 * - spacingScale: spacing rhythm (tight, normal, airy)
 * - motionProfile: animation personality (none, subtle, expressive)
 * - personalityBias: voice/tone hint the LLM should lean into
 *
 * Templates MUST NOT define section order or required sections.
 */

// ---------------------------------------------------------------------------
// Component catalogue – every structural block the LLM can compose
// ---------------------------------------------------------------------------
export const ALL_COMPONENTS = [
  'hero-split',
  'hero-stacked',
  'hero-bold',
  'hero-minimal',
  'hero-video',
  'hero-cards',
  'manifesto-block',
  'timeline-story',
  'credibility-strip',
  'proof-carousel',
  'proof-grid',
  'price-anchor',
  'local-signal',
  'founder-note',
  'service-grid',
  'service-alternating',
  'service-list',
  'service-featured',
  'faq-accordion',
  'cta-banner',
  'cta-inline',
  'stats-row',
  'gallery-mosaic',
  'gallery-strip',
  'quote-highlight',
  'trust-bar',
  'map-embed',
  'hours-block',
  'contact-split',
] as const;

export type ComponentId = (typeof ALL_COMPONENTS)[number];

// ---------------------------------------------------------------------------
// Design system shape
// ---------------------------------------------------------------------------
export interface TypographyTokens {
  fontHeading: string;
  fontBody: string;
  scaleRatio: number; // e.g. 1.25 (major third), 1.333 (perfect fourth)
  headingWeight: number;
  bodyWeight: number;
}

export interface ColorTokens {
  primary: string;
  accent: string;
  bg: string;
  bgAlt: string;
  text: string;
  textMuted: string;
  surface: string;
  border: string;
}

export type SpacingScale = 'tight' | 'normal' | 'airy';
export type MotionProfile = 'none' | 'subtle' | 'expressive';
export type PersonalityBias =
  | 'bold'
  | 'quiet'
  | 'luxury'
  | 'playful'
  | 'clinical'
  | 'warm'
  | 'edgy'
  | 'corporate'
  | 'artisan'
  | 'technical';

export interface DesignSystem {
  id: string;
  name: string;
  category: string;
  allowedComponents: ComponentId[];
  typographyTokens: TypographyTokens;
  colorTokens: ColorTokens;
  spacingScale: SpacingScale;
  motionProfile: MotionProfile;
  personalityBias: PersonalityBias;
  /** Extra CSS that the LLM should include in a <style> tag */
  cssCustomProperties: string;
}

// ---------------------------------------------------------------------------
// Helper: build CSS custom-properties string from tokens
// ---------------------------------------------------------------------------
function buildCssVars(colors: ColorTokens, typo: TypographyTokens, spacing: SpacingScale): string {
  const spacingMap: Record<SpacingScale, { section: string; element: string }> = {
    tight: { section: '48px', element: '16px' },
    normal: { section: '80px', element: '24px' },
    airy: { section: '120px', element: '32px' },
  };
  const s = spacingMap[spacing];
  return [
    `:root {`,
    `  --primary: ${colors.primary};`,
    `  --accent: ${colors.accent};`,
    `  --bg: ${colors.bg};`,
    `  --bg-alt: ${colors.bgAlt};`,
    `  --text: ${colors.text};`,
    `  --text-muted: ${colors.textMuted};`,
    `  --surface: ${colors.surface};`,
    `  --border: ${colors.border};`,
    `  --font-heading: '${typo.fontHeading}', sans-serif;`,
    `  --font-body: '${typo.fontBody}', sans-serif;`,
    `  --scale: ${typo.scaleRatio};`,
    `  --heading-weight: ${typo.headingWeight};`,
    `  --body-weight: ${typo.bodyWeight};`,
    `  --space-section: ${s.section};`,
    `  --space-element: ${s.element};`,
    `}`,
  ].join('\n');
}

// ---------------------------------------------------------------------------
// Design system definitions keyed by template category
// ---------------------------------------------------------------------------

function ds(
  id: string,
  name: string,
  category: string,
  colors: ColorTokens,
  typo: TypographyTokens,
  spacing: SpacingScale,
  motion: MotionProfile,
  personality: PersonalityBias,
  components: ComponentId[],
): DesignSystem {
  return {
    id,
    name,
    category,
    allowedComponents: components,
    typographyTokens: typo,
    colorTokens: colors,
    spacingScale: spacing,
    motionProfile: motion,
    personalityBias: personality,
    cssCustomProperties: buildCssVars(colors, typo, spacing),
  };
}

// Shared component sets per personality
const BOLD_COMPONENTS: ComponentId[] = [
  'hero-bold', 'hero-stacked', 'hero-split',
  'manifesto-block', 'credibility-strip', 'stats-row',
  'service-grid', 'service-featured',
  'proof-carousel', 'proof-grid',
  'cta-banner', 'quote-highlight',
  'local-signal', 'trust-bar',
  'contact-split',
];

const QUIET_COMPONENTS: ComponentId[] = [
  'hero-minimal', 'hero-stacked',
  'manifesto-block', 'founder-note',
  'timeline-story', 'service-list', 'service-alternating',
  'proof-grid', 'quote-highlight',
  'cta-inline', 'local-signal',
  'hours-block', 'faq-accordion',
  'contact-split',
];

const LUXURY_COMPONENTS: ComponentId[] = [
  'hero-split', 'hero-stacked', 'hero-bold',
  'manifesto-block', 'founder-note',
  'gallery-mosaic', 'gallery-strip',
  'proof-carousel', 'quote-highlight',
  'price-anchor', 'credibility-strip',
  'cta-banner', 'local-signal',
  'contact-split',
];

const PLAYFUL_COMPONENTS: ComponentId[] = [
  'hero-cards', 'hero-stacked', 'hero-bold',
  'manifesto-block', 'stats-row',
  'service-grid', 'service-featured',
  'proof-carousel', 'proof-grid',
  'gallery-mosaic', 'cta-banner', 'cta-inline',
  'trust-bar', 'faq-accordion',
  'local-signal', 'contact-split',
];

const WARM_COMPONENTS: ComponentId[] = [
  'hero-split', 'hero-stacked', 'hero-minimal',
  'manifesto-block', 'founder-note', 'timeline-story',
  'service-alternating', 'service-grid',
  'proof-grid', 'proof-carousel', 'quote-highlight',
  'cta-inline', 'local-signal', 'hours-block',
  'gallery-strip', 'map-embed',
  'contact-split',
];

const CORPORATE_COMPONENTS: ComponentId[] = [
  'hero-split', 'hero-stacked', 'hero-minimal',
  'credibility-strip', 'stats-row', 'trust-bar',
  'service-grid', 'service-list',
  'proof-grid', 'proof-carousel',
  'price-anchor', 'cta-banner',
  'faq-accordion', 'contact-split',
];

const TECHNICAL_COMPONENTS: ComponentId[] = [
  'hero-minimal', 'hero-stacked', 'hero-bold',
  'manifesto-block', 'credibility-strip', 'stats-row',
  'service-grid', 'service-list', 'service-featured',
  'proof-grid', 'trust-bar',
  'price-anchor', 'cta-banner', 'cta-inline',
  'faq-accordion', 'contact-split',
];

const ARTISAN_COMPONENTS: ComponentId[] = [
  'hero-split', 'hero-stacked', 'hero-video',
  'manifesto-block', 'founder-note', 'timeline-story',
  'gallery-mosaic', 'gallery-strip',
  'service-alternating', 'service-featured',
  'proof-carousel', 'quote-highlight',
  'local-signal', 'hours-block', 'map-embed',
  'cta-inline', 'contact-split',
];

// ---------------------------------------------------------------------------
// Registry – every template ID maps to a design system
// ---------------------------------------------------------------------------

export const DESIGN_SYSTEMS: Record<string, DesignSystem> = {
  // ── Modern ─────────────────────────────────────────────────────────────
  'modern-gradient': ds('modern-gradient', 'Gradient', 'modern',
    { primary: '#7c3aed', accent: '#a855f7', bg: '#0f0f1a', bgAlt: '#1a1a2e', text: '#ffffff', textMuted: '#a1a1aa', surface: '#1e1e30', border: '#2e2e40' },
    { fontHeading: 'Inter', fontBody: 'Inter', scaleRatio: 1.25, headingWeight: 700, bodyWeight: 400 },
    'normal', 'expressive', 'bold', BOLD_COMPONENTS),

  'modern-glass': ds('modern-glass', 'Glass', 'modern',
    { primary: '#38bdf8', accent: '#0ea5e9', bg: '#0f172a', bgAlt: '#1e293b', text: '#ffffff', textMuted: '#94a3b8', surface: '#1e293b', border: '#334155' },
    { fontHeading: 'Poppins', fontBody: 'Inter', scaleRatio: 1.25, headingWeight: 600, bodyWeight: 400 },
    'normal', 'subtle', 'technical', TECHNICAL_COMPONENTS),

  'modern-neon': ds('modern-neon', 'Neon', 'modern',
    { primary: '#00f5d4', accent: '#f15bb5', bg: '#0a0a0f', bgAlt: '#141420', text: '#ffffff', textMuted: '#a1a1aa', surface: '#16161f', border: '#2a2a3a' },
    { fontHeading: 'Outfit', fontBody: 'Inter', scaleRatio: 1.333, headingWeight: 700, bodyWeight: 400 },
    'tight', 'expressive', 'edgy', BOLD_COMPONENTS),

  'modern-mesh': ds('modern-mesh', 'Mesh', 'modern',
    { primary: '#e11d48', accent: '#f43f5e', bg: '#ffffff', bgAlt: '#fdf2f8', text: '#1f2937', textMuted: '#6b7280', surface: '#ffffff', border: '#e5e7eb' },
    { fontHeading: 'DM Sans', fontBody: 'DM Sans', scaleRatio: 1.25, headingWeight: 700, bodyWeight: 400 },
    'normal', 'subtle', 'warm', WARM_COMPONENTS),

  'modern-aurora': ds('modern-aurora', 'Aurora', 'modern',
    { primary: '#06b6d4', accent: '#14b8a6', bg: '#f0fdfa', bgAlt: '#ccfbf1', text: '#134e4a', textMuted: '#5eead4', surface: '#ffffff', border: '#a7f3d0' },
    { fontHeading: 'Outfit', fontBody: 'Inter', scaleRatio: 1.25, headingWeight: 600, bodyWeight: 400 },
    'airy', 'subtle', 'quiet', QUIET_COMPONENTS),

  // ── Classic ────────────────────────────────────────────────────────────
  'classic-elegant': ds('classic-elegant', 'Elegant', 'classic',
    { primary: '#1e3a5f', accent: '#2563eb', bg: '#faf9f7', bgAlt: '#f3f1ec', text: '#1e3a5f', textMuted: '#64748b', surface: '#ffffff', border: '#e2e0d8' },
    { fontHeading: 'Playfair Display', fontBody: 'Lora', scaleRatio: 1.333, headingWeight: 700, bodyWeight: 400 },
    'airy', 'none', 'luxury', LUXURY_COMPONENTS),

  'classic-traditional': ds('classic-traditional', 'Traditional', 'classic',
    { primary: '#7c2d12', accent: '#c2410c', bg: '#fef7f0', bgAlt: '#fdf0e0', text: '#422006', textMuted: '#92400e', surface: '#ffffff', border: '#fed7aa' },
    { fontHeading: 'Merriweather', fontBody: 'Source Serif Pro', scaleRatio: 1.25, headingWeight: 700, bodyWeight: 400 },
    'normal', 'none', 'warm', WARM_COMPONENTS),

  'classic-vintage': ds('classic-vintage', 'Vintage', 'classic',
    { primary: '#4d7c0f', accent: '#65a30d', bg: '#fefdf8', bgAlt: '#f7f6e7', text: '#365314', textMuted: '#4d7c0f', surface: '#ffffff', border: '#d9f99d' },
    { fontHeading: 'Cormorant Garamond', fontBody: 'EB Garamond', scaleRatio: 1.333, headingWeight: 600, bodyWeight: 400 },
    'airy', 'none', 'artisan', ARTISAN_COMPONENTS),

  'classic-scholarly': ds('classic-scholarly', 'Scholarly', 'classic',
    { primary: '#1e3a8a', accent: '#3b82f6', bg: '#f8fafc', bgAlt: '#eff6ff', text: '#1e3a8a', textMuted: '#3b82f6', surface: '#ffffff', border: '#bfdbfe' },
    { fontHeading: 'Libre Baskerville', fontBody: 'Crimson Text', scaleRatio: 1.25, headingWeight: 700, bodyWeight: 400 },
    'normal', 'none', 'corporate', CORPORATE_COMPONENTS),

  'classic-heritage': ds('classic-heritage', 'Heritage', 'classic',
    { primary: '#44403c', accent: '#78716c', bg: '#fafaf9', bgAlt: '#f5f5f4', text: '#292524', textMuted: '#78716c', surface: '#ffffff', border: '#d6d3d1' },
    { fontHeading: 'Spectral', fontBody: 'Spectral', scaleRatio: 1.25, headingWeight: 600, bodyWeight: 400 },
    'airy', 'none', 'quiet', QUIET_COMPONENTS),

  // ── Bold ───────────────────────────────────────────────────────────────
  'bold-fire': ds('bold-fire', 'Fire', 'bold',
    { primary: '#dc2626', accent: '#f97316', bg: '#0c0a09', bgAlt: '#1c1917', text: '#ffffff', textMuted: '#a8a29e', surface: '#1c1917', border: '#44403c' },
    { fontHeading: 'Bebas Neue', fontBody: 'Inter', scaleRatio: 1.5, headingWeight: 400, bodyWeight: 400 },
    'tight', 'expressive', 'edgy', BOLD_COMPONENTS),

  'bold-electric': ds('bold-electric', 'Electric', 'bold',
    { primary: '#06b6d4', accent: '#3b82f6', bg: '#020617', bgAlt: '#0f172a', text: '#ffffff', textMuted: '#94a3b8', surface: '#0f172a', border: '#1e293b' },
    { fontHeading: 'Righteous', fontBody: 'Inter', scaleRatio: 1.333, headingWeight: 400, bodyWeight: 400 },
    'normal', 'expressive', 'bold', BOLD_COMPONENTS),

  'bold-midnight': ds('bold-midnight', 'Midnight', 'bold',
    { primary: '#6366f1', accent: '#8b5cf6', bg: '#0c0a1d', bgAlt: '#1a1830', text: '#ffffff', textMuted: '#a5b4fc', surface: '#1a1830', border: '#312e81' },
    { fontHeading: 'Montserrat', fontBody: 'Montserrat', scaleRatio: 1.25, headingWeight: 800, bodyWeight: 400 },
    'normal', 'subtle', 'bold', BOLD_COMPONENTS),

  'bold-impact': ds('bold-impact', 'Impact', 'bold',
    { primary: '#18181b', accent: '#eab308', bg: '#ffffff', bgAlt: '#f4f4f5', text: '#18181b', textMuted: '#71717a', surface: '#ffffff', border: '#e4e4e7' },
    { fontHeading: 'Anton', fontBody: 'Inter', scaleRatio: 1.5, headingWeight: 400, bodyWeight: 400 },
    'tight', 'subtle', 'bold', BOLD_COMPONENTS),

  'bold-contrast': ds('bold-contrast', 'Contrast', 'bold',
    { primary: '#e11d48', accent: '#f43f5e', bg: '#ffffff', bgAlt: '#fff1f2', text: '#18181b', textMuted: '#71717a', surface: '#ffffff', border: '#fecdd3' },
    { fontHeading: 'Archivo Black', fontBody: 'Archivo', scaleRatio: 1.333, headingWeight: 400, bodyWeight: 400 },
    'normal', 'subtle', 'edgy', BOLD_COMPONENTS),

  // ── Minimal ────────────────────────────────────────────────────────────
  'minimal-clean': ds('minimal-clean', 'Clean', 'minimal',
    { primary: '#374151', accent: '#3b82f6', bg: '#ffffff', bgAlt: '#f9fafb', text: '#1f2937', textMuted: '#6b7280', surface: '#ffffff', border: '#e5e7eb' },
    { fontHeading: 'Inter', fontBody: 'Inter', scaleRatio: 1.2, headingWeight: 600, bodyWeight: 400 },
    'airy', 'none', 'quiet', QUIET_COMPONENTS),

  'minimal-mono': ds('minimal-mono', 'Mono', 'minimal',
    { primary: '#27272a', accent: '#18181b', bg: '#fafafa', bgAlt: '#f4f4f5', text: '#27272a', textMuted: '#71717a', surface: '#ffffff', border: '#d4d4d8' },
    { fontHeading: 'Space Mono', fontBody: 'IBM Plex Mono', scaleRatio: 1.2, headingWeight: 700, bodyWeight: 400 },
    'normal', 'none', 'technical', TECHNICAL_COMPONENTS),

  'minimal-zen': ds('minimal-zen', 'Zen', 'minimal',
    { primary: '#78716c', accent: '#a8a29e', bg: '#fafaf9', bgAlt: '#f5f5f4', text: '#44403c', textMuted: '#78716c', surface: '#ffffff', border: '#d6d3d1' },
    { fontHeading: 'Noto Serif', fontBody: 'Noto Sans', scaleRatio: 1.25, headingWeight: 500, bodyWeight: 400 },
    'airy', 'none', 'quiet', QUIET_COMPONENTS),

  'minimal-nordic': ds('minimal-nordic', 'Nordic', 'minimal',
    { primary: '#475569', accent: '#0ea5e9', bg: '#f8fafc', bgAlt: '#f1f5f9', text: '#334155', textMuted: '#64748b', surface: '#ffffff', border: '#cbd5e1' },
    { fontHeading: 'Quicksand', fontBody: 'Nunito', scaleRatio: 1.25, headingWeight: 600, bodyWeight: 400 },
    'airy', 'subtle', 'quiet', QUIET_COMPONENTS),

  'minimal-paper': ds('minimal-paper', 'Paper', 'minimal',
    { primary: '#64748b', accent: '#dc2626', bg: '#ffffff', bgAlt: '#f8fafc', text: '#475569', textMuted: '#94a3b8', surface: '#ffffff', border: '#e2e8f0' },
    { fontHeading: 'Karla', fontBody: 'Karla', scaleRatio: 1.2, headingWeight: 700, bodyWeight: 400 },
    'normal', 'none', 'quiet', QUIET_COMPONENTS),

  // ── Creative ───────────────────────────────────────────────────────────
  'creative-playful': ds('creative-playful', 'Playful', 'creative',
    { primary: '#ec4899', accent: '#06b6d4', bg: '#ffffff', bgAlt: '#fce7f3', text: '#4c1d95', textMuted: '#7c3aed', surface: '#ffffff', border: '#f9a8d4' },
    { fontHeading: 'Baloo 2', fontBody: 'Nunito', scaleRatio: 1.333, headingWeight: 700, bodyWeight: 400 },
    'normal', 'expressive', 'playful', PLAYFUL_COMPONENTS),

  'creative-artsy': ds('creative-artsy', 'Artsy', 'creative',
    { primary: '#8b5cf6', accent: '#d946ef', bg: '#faf5ff', bgAlt: '#f3e8ff', text: '#581c87', textMuted: '#7c3aed', surface: '#ffffff', border: '#c4b5fd' },
    { fontHeading: 'Pacifico', fontBody: 'Quicksand', scaleRatio: 1.333, headingWeight: 400, bodyWeight: 500 },
    'airy', 'expressive', 'playful', PLAYFUL_COMPONENTS),

  'creative-retro': ds('creative-retro', 'Retro', 'creative',
    { primary: '#db2777', accent: '#f97316', bg: '#fdf4ff', bgAlt: '#fce7f3', text: '#831843', textMuted: '#be185d', surface: '#ffffff', border: '#f9a8d4' },
    { fontHeading: 'Bungee', fontBody: 'Inter', scaleRatio: 1.5, headingWeight: 400, bodyWeight: 400 },
    'tight', 'expressive', 'edgy', BOLD_COMPONENTS),

  'creative-pop': ds('creative-pop', 'Pop', 'creative',
    { primary: '#84cc16', accent: '#22c55e', bg: '#ffffff', bgAlt: '#f0fdf4', text: '#1a2e05', textMuted: '#4d7c0f', surface: '#ffffff', border: '#bbf7d0' },
    { fontHeading: 'Bangers', fontBody: 'Nunito', scaleRatio: 1.5, headingWeight: 400, bodyWeight: 400 },
    'normal', 'expressive', 'playful', PLAYFUL_COMPONENTS),

  'creative-brush': ds('creative-brush', 'Brush', 'creative',
    { primary: '#f43f5e', accent: '#fb7185', bg: '#fff1f2', bgAlt: '#ffe4e6', text: '#881337', textMuted: '#be123c', surface: '#ffffff', border: '#fecdd3' },
    { fontHeading: 'Caveat', fontBody: 'Inter', scaleRatio: 1.333, headingWeight: 700, bodyWeight: 400 },
    'airy', 'subtle', 'artisan', ARTISAN_COMPONENTS),

  // ── Corporate ──────────────────────────────────────────────────────────
  'corporate-professional': ds('corporate-professional', 'Professional', 'corporate',
    { primary: '#1e40af', accent: '#3b82f6', bg: '#ffffff', bgAlt: '#eff6ff', text: '#1e3a8a', textMuted: '#3b82f6', surface: '#ffffff', border: '#bfdbfe' },
    { fontHeading: 'Roboto', fontBody: 'Roboto', scaleRatio: 1.25, headingWeight: 700, bodyWeight: 400 },
    'normal', 'none', 'corporate', CORPORATE_COMPONENTS),

  'corporate-executive': ds('corporate-executive', 'Executive', 'corporate',
    { primary: '#1e293b', accent: '#f59e0b', bg: '#f8fafc', bgAlt: '#f1f5f9', text: '#0f172a', textMuted: '#475569', surface: '#ffffff', border: '#e2e8f0' },
    { fontHeading: 'Libre Franklin', fontBody: 'Inter', scaleRatio: 1.25, headingWeight: 700, bodyWeight: 400 },
    'normal', 'none', 'corporate', CORPORATE_COMPONENTS),

  'corporate-startup': ds('corporate-startup', 'Startup', 'corporate',
    { primary: '#6366f1', accent: '#8b5cf6', bg: '#ffffff', bgAlt: '#eef2ff', text: '#312e81', textMuted: '#6366f1', surface: '#ffffff', border: '#c7d2fe' },
    { fontHeading: 'Plus Jakarta Sans', fontBody: 'Plus Jakarta Sans', scaleRatio: 1.25, headingWeight: 700, bodyWeight: 400 },
    'normal', 'subtle', 'bold', BOLD_COMPONENTS),

  'corporate-consulting': ds('corporate-consulting', 'Consulting', 'corporate',
    { primary: '#334155', accent: '#be185d', bg: '#f8fafc', bgAlt: '#f1f5f9', text: '#1e293b', textMuted: '#64748b', surface: '#ffffff', border: '#e2e8f0' },
    { fontHeading: 'Source Sans Pro', fontBody: 'Source Sans Pro', scaleRatio: 1.2, headingWeight: 600, bodyWeight: 400 },
    'normal', 'none', 'corporate', CORPORATE_COMPONENTS),

  'corporate-finance': ds('corporate-finance', 'Finance', 'corporate',
    { primary: '#065f46', accent: '#10b981', bg: '#ffffff', bgAlt: '#ecfdf5', text: '#064e3b', textMuted: '#047857', surface: '#ffffff', border: '#a7f3d0' },
    { fontHeading: 'Lato', fontBody: 'Lato', scaleRatio: 1.2, headingWeight: 700, bodyWeight: 400 },
    'normal', 'none', 'corporate', CORPORATE_COMPONENTS),

  // ── Luxury ─────────────────────────────────────────────────────────────
  'luxury-gold': ds('luxury-gold', 'Gold', 'luxury',
    { primary: '#ca8a04', accent: '#fbbf24', bg: '#0c0a09', bgAlt: '#1c1917', text: '#fef3c7', textMuted: '#d97706', surface: '#1c1917', border: '#44403c' },
    { fontHeading: 'Cinzel', fontBody: 'Cormorant', scaleRatio: 1.333, headingWeight: 700, bodyWeight: 400 },
    'airy', 'subtle', 'luxury', LUXURY_COMPONENTS),

  'luxury-noir': ds('luxury-noir', 'Noir', 'luxury',
    { primary: '#f5f5f5', accent: '#d4d4d4', bg: '#0a0a0a', bgAlt: '#171717', text: '#fafafa', textMuted: '#a3a3a3', surface: '#171717', border: '#262626' },
    { fontHeading: 'Italiana', fontBody: 'Raleway', scaleRatio: 1.333, headingWeight: 400, bodyWeight: 300 },
    'airy', 'subtle', 'luxury', LUXURY_COMPONENTS),

  'luxury-marble': ds('luxury-marble', 'Marble', 'luxury',
    { primary: '#57534e', accent: '#ca8a04', bg: '#fafaf9', bgAlt: '#f5f5f4', text: '#292524', textMuted: '#78716c', surface: '#ffffff', border: '#d6d3d1' },
    { fontHeading: 'Cormorant Garamond', fontBody: 'Nunito Sans', scaleRatio: 1.333, headingWeight: 600, bodyWeight: 400 },
    'airy', 'none', 'luxury', LUXURY_COMPONENTS),

  'luxury-royal': ds('luxury-royal', 'Royal', 'luxury',
    { primary: '#7e22ce', accent: '#eab308', bg: '#0c0015', bgAlt: '#1a0030', text: '#f3e8ff', textMuted: '#c084fc', surface: '#1a0030', border: '#581c87' },
    { fontHeading: 'Bodoni Moda', fontBody: 'EB Garamond', scaleRatio: 1.333, headingWeight: 700, bodyWeight: 400 },
    'airy', 'subtle', 'luxury', LUXURY_COMPONENTS),

  'luxury-champagne': ds('luxury-champagne', 'Champagne', 'luxury',
    { primary: '#a16207', accent: '#78716c', bg: '#fef9ef', bgAlt: '#fef3c7', text: '#451a03', textMuted: '#92400e', surface: '#ffffff', border: '#fde68a' },
    { fontHeading: 'Tenor Sans', fontBody: 'Josefin Sans', scaleRatio: 1.25, headingWeight: 400, bodyWeight: 400 },
    'airy', 'none', 'luxury', LUXURY_COMPONENTS),

  // ── Tech ───────────────────────────────────────────────────────────────
  'tech-cyber': ds('tech-cyber', 'Cyber', 'tech',
    { primary: '#10b981', accent: '#06b6d4', bg: '#020617', bgAlt: '#0f172a', text: '#f0fdf4', textMuted: '#6ee7b7', surface: '#0f172a', border: '#064e3b' },
    { fontHeading: 'Orbitron', fontBody: 'Inter', scaleRatio: 1.25, headingWeight: 700, bodyWeight: 400 },
    'tight', 'expressive', 'technical', TECHNICAL_COMPONENTS),

  'tech-matrix': ds('tech-matrix', 'Matrix', 'tech',
    { primary: '#22c55e', accent: '#16a34a', bg: '#020408', bgAlt: '#0a1a10', text: '#4ade80', textMuted: '#22c55e', surface: '#0a1a10', border: '#14532d' },
    { fontHeading: 'Share Tech Mono', fontBody: 'IBM Plex Mono', scaleRatio: 1.2, headingWeight: 700, bodyWeight: 400 },
    'tight', 'subtle', 'technical', TECHNICAL_COMPONENTS),

  'tech-ai': ds('tech-ai', 'AI', 'tech',
    { primary: '#8b5cf6', accent: '#d946ef', bg: '#0c0a1d', bgAlt: '#1a1830', text: '#f5f3ff', textMuted: '#a78bfa', surface: '#1a1830', border: '#4c1d95' },
    { fontHeading: 'Space Grotesk', fontBody: 'Inter', scaleRatio: 1.25, headingWeight: 700, bodyWeight: 400 },
    'normal', 'expressive', 'technical', TECHNICAL_COMPONENTS),

  'tech-terminal': ds('tech-terminal', 'Terminal', 'tech',
    { primary: '#22c55e', accent: '#0ea5e9', bg: '#0f172a', bgAlt: '#1e293b', text: '#4ade80', textMuted: '#22c55e', surface: '#1e293b', border: '#334155' },
    { fontHeading: 'JetBrains Mono', fontBody: 'Fira Code', scaleRatio: 1.2, headingWeight: 700, bodyWeight: 400 },
    'tight', 'none', 'technical', TECHNICAL_COMPONENTS),

  'tech-dark': ds('tech-dark', 'Dark', 'tech',
    { primary: '#3b82f6', accent: '#60a5fa', bg: '#09090b', bgAlt: '#18181b', text: '#e4e4e7', textMuted: '#a1a1aa', surface: '#18181b', border: '#27272a' },
    { fontHeading: 'Inter', fontBody: 'Inter', scaleRatio: 1.25, headingWeight: 700, bodyWeight: 400 },
    'normal', 'subtle', 'technical', TECHNICAL_COMPONENTS),

  // ── Health ─────────────────────────────────────────────────────────────
  'health-wellness': ds('health-wellness', 'Wellness', 'health',
    { primary: '#10b981', accent: '#34d399', bg: '#f0fdf4', bgAlt: '#dcfce7', text: '#064e3b', textMuted: '#059669', surface: '#ffffff', border: '#a7f3d0' },
    { fontHeading: 'Comfortaa', fontBody: 'Nunito', scaleRatio: 1.25, headingWeight: 700, bodyWeight: 400 },
    'airy', 'subtle', 'warm', WARM_COMPONENTS),

  'health-medical': ds('health-medical', 'Medical', 'health',
    { primary: '#0284c7', accent: '#0ea5e9', bg: '#ffffff', bgAlt: '#f0f9ff', text: '#0c4a6e', textMuted: '#0284c7', surface: '#ffffff', border: '#bae6fd' },
    { fontHeading: 'Roboto', fontBody: 'Open Sans', scaleRatio: 1.2, headingWeight: 700, bodyWeight: 400 },
    'normal', 'none', 'clinical', CORPORATE_COMPONENTS),

  'health-spa': ds('health-spa', 'Spa', 'health',
    { primary: '#d6b98a', accent: '#14b8a6', bg: '#fefce8', bgAlt: '#fef9c3', text: '#422006', textMuted: '#92400e', surface: '#ffffff', border: '#fde68a' },
    { fontHeading: 'Marcellus', fontBody: 'Lato', scaleRatio: 1.333, headingWeight: 400, bodyWeight: 400 },
    'airy', 'subtle', 'luxury', LUXURY_COMPONENTS),

  'health-fitness': ds('health-fitness', 'Fitness', 'health',
    { primary: '#dc2626', accent: '#f97316', bg: '#0c0a09', bgAlt: '#1c1917', text: '#ffffff', textMuted: '#a8a29e', surface: '#1c1917', border: '#44403c' },
    { fontHeading: 'Teko', fontBody: 'Inter', scaleRatio: 1.5, headingWeight: 600, bodyWeight: 400 },
    'tight', 'expressive', 'bold', BOLD_COMPONENTS),

  'health-natural': ds('health-natural', 'Natural', 'health',
    { primary: '#16a34a', accent: '#4ade80', bg: '#f0fdf4', bgAlt: '#dcfce7', text: '#14532d', textMuted: '#16a34a', surface: '#ffffff', border: '#bbf7d0' },
    { fontHeading: 'Quicksand', fontBody: 'Nunito', scaleRatio: 1.25, headingWeight: 600, bodyWeight: 400 },
    'airy', 'subtle', 'warm', WARM_COMPONENTS),

  // ── Food ───────────────────────────────────────────────────────────────
  'food-restaurant': ds('food-restaurant', 'Restaurant', 'food',
    { primary: '#b91c1c', accent: '#ca8a04', bg: '#0c0a09', bgAlt: '#1c1917', text: '#fef2f2', textMuted: '#fca5a5', surface: '#1c1917', border: '#44403c' },
    { fontHeading: 'Playfair Display', fontBody: 'Lato', scaleRatio: 1.333, headingWeight: 700, bodyWeight: 400 },
    'normal', 'subtle', 'artisan', ARTISAN_COMPONENTS),

  'food-cafe': ds('food-cafe', 'Café', 'food',
    { primary: '#a16207', accent: '#ca8a04', bg: '#fefce8', bgAlt: '#fef9c3', text: '#422006', textMuted: '#92400e', surface: '#ffffff', border: '#fde68a' },
    { fontHeading: 'Satisfy', fontBody: 'Raleway', scaleRatio: 1.25, headingWeight: 400, bodyWeight: 400 },
    'airy', 'subtle', 'artisan', ARTISAN_COMPONENTS),

  'food-bakery': ds('food-bakery', 'Bakery', 'food',
    { primary: '#ea580c', accent: '#f97316', bg: '#fff7ed', bgAlt: '#ffedd5', text: '#431407', textMuted: '#c2410c', surface: '#ffffff', border: '#fed7aa' },
    { fontHeading: 'Cookie', fontBody: 'Poppins', scaleRatio: 1.333, headingWeight: 400, bodyWeight: 400 },
    'normal', 'subtle', 'warm', WARM_COMPONENTS),

  'food-fine-dining': ds('food-fine-dining', 'Fine Dining', 'food',
    { primary: '#f5f5f4', accent: '#ca8a04', bg: '#0c0a09', bgAlt: '#1c1917', text: '#f5f5f4', textMuted: '#a8a29e', surface: '#1c1917', border: '#44403c' },
    { fontHeading: 'Cormorant', fontBody: 'Montserrat', scaleRatio: 1.333, headingWeight: 600, bodyWeight: 300 },
    'airy', 'subtle', 'luxury', LUXURY_COMPONENTS),

  'food-delivery': ds('food-delivery', 'Delivery', 'food',
    { primary: '#dc2626', accent: '#f97316', bg: '#ffffff', bgAlt: '#fef2f2', text: '#1c1917', textMuted: '#78716c', surface: '#ffffff', border: '#fecaca' },
    { fontHeading: 'Fredoka One', fontBody: 'Nunito', scaleRatio: 1.333, headingWeight: 400, bodyWeight: 400 },
    'normal', 'expressive', 'playful', PLAYFUL_COMPONENTS),

  // ── Legacy compatibility ───────────────────────────────────────────────
  'modern-minimal': ds('modern-minimal', 'Modern Minimal', 'legacy',
    { primary: '#0f172a', accent: '#3b82f6', bg: '#ffffff', bgAlt: '#f8fafc', text: '#1f2937', textMuted: '#6b7280', surface: '#ffffff', border: '#e5e7eb' },
    { fontHeading: 'Inter', fontBody: 'Inter', scaleRatio: 1.2, headingWeight: 600, bodyWeight: 400 },
    'airy', 'none', 'quiet', QUIET_COMPONENTS),

  'bold-corporate': ds('bold-corporate', 'Bold Corporate', 'legacy',
    { primary: '#1e3a5f', accent: '#f59e0b', bg: '#ffffff', bgAlt: '#f8fafc', text: '#1f2937', textMuted: '#6b7280', surface: '#ffffff', border: '#e5e7eb' },
    { fontHeading: 'Montserrat', fontBody: 'Montserrat', scaleRatio: 1.25, headingWeight: 700, bodyWeight: 400 },
    'normal', 'none', 'corporate', CORPORATE_COMPONENTS),

  'warm-friendly': ds('warm-friendly', 'Warm Friendly', 'legacy',
    { primary: '#7c2d12', accent: '#fb923c', bg: '#ffffff', bgAlt: '#fff7ed', text: '#1f2937', textMuted: '#6b7280', surface: '#ffffff', border: '#fed7aa' },
    { fontHeading: 'Nunito', fontBody: 'Nunito', scaleRatio: 1.25, headingWeight: 700, bodyWeight: 400 },
    'normal', 'subtle', 'warm', WARM_COMPONENTS),

  'tech-startup': ds('tech-startup', 'Tech Startup', 'legacy',
    { primary: '#0f172a', accent: '#06b6d4', bg: '#ffffff', bgAlt: '#f0fdfa', text: '#1f2937', textMuted: '#6b7280', surface: '#ffffff', border: '#a5f3fc' },
    { fontHeading: 'Space Grotesk', fontBody: 'Inter', scaleRatio: 1.25, headingWeight: 700, bodyWeight: 400 },
    'normal', 'subtle', 'technical', TECHNICAL_COMPONENTS),

  'elegant-luxury': ds('elegant-luxury', 'Elegant Luxury', 'legacy',
    { primary: '#1c1917', accent: '#d4af37', bg: '#0c0a09', bgAlt: '#1c1917', text: '#fafaf9', textMuted: '#a8a29e', surface: '#1c1917', border: '#44403c' },
    { fontHeading: 'Playfair Display', fontBody: 'Lora', scaleRatio: 1.333, headingWeight: 700, bodyWeight: 400 },
    'airy', 'subtle', 'luxury', LUXURY_COMPONENTS),
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function getDesignSystem(templateId: string): DesignSystem {
  return DESIGN_SYSTEMS[templateId] || DESIGN_SYSTEMS['minimal-clean'];
}

export function getDesignSystemForPrompt(templateId: string): string {
  const ds = getDesignSystem(templateId);
  return [
    `DESIGN SYSTEM: "${ds.name}" (${ds.category})`,
    `PERSONALITY: ${ds.personalityBias}`,
    `SPACING: ${ds.spacingScale}`,
    `MOTION: ${ds.motionProfile}`,
    '',
    `ALLOWED COMPONENTS (use ONLY these):`,
    ds.allowedComponents.map(c => `  - ${c}`).join('\n'),
    '',
    `TYPOGRAPHY:`,
    `  Heading: ${ds.typographyTokens.fontHeading} (weight ${ds.typographyTokens.headingWeight})`,
    `  Body: ${ds.typographyTokens.fontBody} (weight ${ds.typographyTokens.bodyWeight})`,
    `  Scale ratio: ${ds.typographyTokens.scaleRatio}`,
    '',
    `COLOR TOKENS (use these as CSS variables):`,
    `  --primary: ${ds.colorTokens.primary}`,
    `  --accent: ${ds.colorTokens.accent}`,
    `  --bg: ${ds.colorTokens.bg}`,
    `  --bg-alt: ${ds.colorTokens.bgAlt}`,
    `  --text: ${ds.colorTokens.text}`,
    `  --text-muted: ${ds.colorTokens.textMuted}`,
    `  --surface: ${ds.colorTokens.surface}`,
    `  --border: ${ds.colorTokens.border}`,
    '',
    `CSS CUSTOM PROPERTIES (include in <style>):`,
    ds.cssCustomProperties,
  ].join('\n');
}
